/* ── Sidebar ─────────────────────────────────────────────────────────────
   Light: warm parchment (aged chart log cover, "buku log kapal siang hari")
   Dark:  deep ocean teal-navy ("chart room below deck, malam hari")
   Both share the same ornament language: Southern Cross stars + depth contours.
   Split-tone layout: textured sidebar vs bright cream content. */

.sidebar {
  background: light-dark(
    linear-gradient(175deg, #e8ddc6 0%, #e1d4b8 55%, #d9cbab 100%),
    linear-gradient(175deg, #162d3e 0%, #0e1f2c 55%, #091620 100%)
  );
  border-right: none;
}

/* z-index overrides */
:global(.mantine-AppShell-navbar) {
  z-index: 1001;
}
:global(.mantine-AppShell-header) {
  z-index: 1002;
}

/* Page body gradient */
:global([data-mantine-color-scheme="light"]) :global(.mantine-AppShell-main) {
  background: linear-gradient(145deg, #f8f2e7 0%, #f4ecdb 55%, #ede0c4 100%);
}
:global([data-mantine-color-scheme="dark"]) :global(.mantine-AppShell-main) {
  background: linear-gradient(145deg, #0d1f28 0%, #0a1820 45%, #071218 100%);
}

/* ── Brand area ────────────────────────────────────────────────────────── */

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  /* Gold horizon line */
  border-bottom: 1px solid light-dark(rgba(168, 135, 75, 0.25), rgba(212, 182, 108, 0.2));
  min-height: 60px;
  position: relative;
  overflow: hidden;
}

/* Southern Cross navigation stars
   Light: very faint navy dots on parchment
   Dark:  faint cyan dots on ocean */
.brand::after {
  content: "";
  position: absolute;
  top: 6px;
  right: 10px;
  width: 52px;
  height: 44px;
  background-image:
    radial-gradient(
      circle 1.8px at 26px 6px,
      light-dark(rgba(26, 53, 72, 0.18), rgba(180, 230, 230, 0.55)) 100%,
      transparent 0
    ),
    radial-gradient(
      circle 1.2px at 16px 17px,
      light-dark(rgba(26, 53, 72, 0.13), rgba(180, 230, 230, 0.4)) 100%,
      transparent 0
    ),
    radial-gradient(
      circle 1.4px at 36px 17px,
      light-dark(rgba(26, 53, 72, 0.15), rgba(180, 230, 230, 0.45)) 100%,
      transparent 0
    ),
    radial-gradient(
      circle 1.6px at 26px 28px,
      light-dark(rgba(26, 53, 72, 0.16), rgba(180, 230, 230, 0.5)) 100%,
      transparent 0
    ),
    radial-gradient(
      circle 0.9px at 44px 10px,
      light-dark(rgba(26, 53, 72, 0.1), rgba(180, 230, 230, 0.3)) 100%,
      transparent 0
    ),
    radial-gradient(
      circle 0.9px at 8px 24px,
      light-dark(rgba(26, 53, 72, 0.1), rgba(180, 230, 230, 0.28)) 100%,
      transparent 0
    );
  pointer-events: none;
}

.brandIcon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: light-dark(linear-gradient(135deg, #0e7c7b, #084948), linear-gradient(135deg, #1a9c98, #0b6362));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: light-dark(
    0 2px 6px rgba(8, 73, 72, 0.3),
    0 2px 8px rgba(10, 30, 40, 0.5) 0 0 0 1px rgba(58, 175, 171, 0.25)
  );
  flex-shrink: 0;
}

.brandText {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: -0.005em;
  color: light-dark(#1a3548, rgba(220, 242, 244, 0.95));
}

/* ── Avatar toggle buttons ──────────────────────────────────────────────── */

.expandAvatarChip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px 3px 3px;
  border-radius: 20px;
  background: light-dark(rgba(14, 124, 123, 0.12), rgba(14, 124, 123, 0.22));
  transition: background 140ms ease;
  flex-shrink: 0;
}

.expandAvatarChip:hover {
  background: light-dark(rgba(14, 124, 123, 0.22), rgba(14, 124, 123, 0.38));
}

.chipIcon {
  color: light-dark(rgba(11, 99, 98, 0.75), rgba(120, 210, 208, 0.8));
  opacity: 1;
}

.collapseAvatarBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 11px 0;
  position: relative;
}

.collapseAvatar {
  transition: transform 120ms ease;
}

.collapseAvatarBtn:hover .collapseAvatar {
  transform: scale(1.06);
}

.collapseAvatarDot {
  position: absolute;
  bottom: 6px;
  right: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: light-dark(#0e7c7b, #1a9c98);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

/* ── Nav list ─────────────────────────────────────────────────────────── */

.navList {
  padding: 8px 8px;
  position: relative;
}

/* Depth contour bathymetric pattern — bottom of nav */
.navList::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 110px;
  background-image:
    radial-gradient(
      ellipse 240px 80px at 50% 130px,
      transparent 58%,
      light-dark(rgba(14, 99, 98, 0.06), rgba(58, 175, 171, 0.07)) 59%,
      transparent 62%
    ),
    radial-gradient(
      ellipse 200px 68px at 50% 130px,
      transparent 56%,
      light-dark(rgba(14, 99, 98, 0.06), rgba(58, 175, 171, 0.07)) 57%,
      transparent 60%
    ),
    radial-gradient(
      ellipse 156px 54px at 50% 130px,
      transparent 53%,
      light-dark(rgba(14, 99, 98, 0.06), rgba(58, 175, 171, 0.07)) 54%,
      transparent 57%
    ),
    radial-gradient(
      ellipse 110px 38px at 50% 130px,
      transparent 49%,
      light-dark(rgba(14, 99, 98, 0.07), rgba(58, 175, 171, 0.08)) 50%,
      transparent 54%
    );
  pointer-events: none;
}

.navSection {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: light-dark(rgba(90, 110, 100, 0.5), rgba(100, 158, 172, 0.55));
  padding: 14px 12px 4px;
  user-select: none;
}

/* ── Footer ─────────────────────────────────────────────────────────────── */

.footer {
  border-top: 1px solid light-dark(rgba(140, 110, 60, 0.18), rgba(58, 175, 171, 0.15));
  padding: 10px;
  background: light-dark(rgba(0, 0, 0, 0.06), rgba(5, 12, 18, 0.25));
}

.footerExpanded {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.userCard {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms ease;
  text-align: left;
  text-decoration: none;
}

.userCard:hover {
  background: light-dark(rgba(14, 124, 123, 0.1), rgba(14, 124, 123, 0.14));
}

.userInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.footerActions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding: 0 2px;
}

.footerAvatarBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 7px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms ease;
}

.footerAvatarBtn:hover {
  background: light-dark(rgba(14, 124, 123, 0.1), rgba(14, 124, 123, 0.14));
}

/* ── Nav items ───────────────────────────────────────────────────────────── */

.navItem {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  color: light-dark(rgba(30, 55, 70, 0.68), rgba(188, 218, 228, 0.72));
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  transition:
    background 140ms ease,
    color 140ms ease;
  margin-bottom: 1px;
}

.navItem:hover {
  background: light-dark(rgba(14, 124, 123, 0.1), rgba(14, 124, 123, 0.14));
  color: light-dark(rgba(8, 73, 72, 0.95), rgba(220, 242, 244, 0.95));
}

/* Top-level leaf active */
.navItem[data-active="true"] {
  color: light-dark(#0b6362, #7dd3d1);
  font-weight: 600;
}

/* Parent group with active child */
.navItem[data-group="true"][data-active="true"] {
  color: light-dark(#0b6362, #7dd3d1);
  font-weight: 600;
}

.navItem[data-group="true"][data-active="true"]:hover {
  background: light-dark(rgba(14, 124, 123, 0.1), rgba(14, 124, 123, 0.14));
}

/* Sub-item */
.navChild {
  padding: 6px 12px 6px 30px;
  font-size: 0.83rem;
  color: light-dark(rgba(40, 70, 80, 0.52), rgba(160, 200, 214, 0.58));
  gap: 8px;
}

.navChildDot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: light-dark(rgba(80, 110, 100, 0.35), rgba(100, 158, 172, 0.4));
  flex-shrink: 0;
}

/* Sub-item active */
.navItem.navChild[data-active="true"] {
  color: light-dark(#0b6362, #7dd3d1);
  background: light-dark(rgba(14, 124, 123, 0.12), rgba(14, 124, 123, 0.2));
  font-weight: 600;
}

.navItem.navChild[data-active="true"] .navChildDot {
  background: light-dark(#0e7c7b, #3aafab);
}

/* Collapsed: parent icon carries active state */
.collapsed .navItem[data-group="true"][data-active="true"] {
  color: light-dark(#0b6362, #7dd3d1);
  font-weight: 600;
}

.chevron {
  margin-left: auto;
  transition: transform 180ms ease;
  opacity: 0.4;
  flex-shrink: 0;
}

.chevron[data-expanded="true"] {
  transform: rotate(90deg);
}

.collapsed .navItem {
  justify-content: center;
  padding: 10px 0;
}
